import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";
import React from "react";
import { render } from "react-dom";
import { Router, Route, IndexRoute, Redirect, browserHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import { persistStore } from "redux-persist";
import { loadAccts } from "./domain/accts/acctsActions";
import { fetchQuestions, checkLoggedInStatus } from "./domain/auth/authActions";
import ga from "./domain/ga";
import App from "./AppContainer";
import Public from "./PublicContainer";
import Secured from "./SecuredContainer";
import ContactSecure from "./components/containers/secure/ContactSecure/ContactSecure";
import ContactPublic from "./components/containers/public/ContactPublic";
import Dashboard from "./components/containers/secure/Dashboard/Dashboard";
import LegalNotice from "./components/containers/public/legal/LegalNotice/LegalNotice";
import Unsubscribe from "./components/containers/public/Unsubscribe/Unsubscribe";
import Accessibility from "./components/containers/public/legal/Accessibility/Accessibility";
import Login from "./components/containers/public/Login/Login";
import NewUserOptions from "./components/containers/public/Login/NewUserOptions";
import PasswordChange from "./components/containers/secure/PasswordChange/PasswordChange";
import PasswordCreate from "./components/containers/public/PasswordCreate/PasswordCreate";
import PasswordForgot from "./components/containers/public/PasswordForgot";
import PayNow from "./components/containers/secure/PayNow/PayNow";
import ProfileCreate from "./components/containers/secure/ProfileCreate";
import ProfileEdit from "./components/containers/secure/ProfileEdit/ProfileEdit";
import ReferAuthorizedUser from "./components/containers/public/ReferAuthorizedUser/ReferAuthorizedUser";
import ThankYou from "./components/containers/public/ThankYou";
import ThankYouRefer from "./components/containers/public/ThankYouRefer";
import Receipt from "./components/containers/secure/Receipt/Receipt";
import { importLocale } from "./domain/i18n/i18nActions";
import * as i18nUtils from "./domain/i18n/utils";
import store from "./appStore";
import "../styles/modern.less";
import Disclosures from "./components/containers/public/legal/Disclosures/Disclosures";
import Account from "./components/containers/secure/Account/Account";
import Callback from "./components/containers/secure/Callback/Callback";
import Settlement from "./components/containers/secure/Settlement/SettlementWrapper";
import StoreProvider from "./components/containers/StoreProvider";
import QuickPay from "./components/containers/public/QuickPay/QuickPay";
import ThankYouEmailSecure from "./components/containers/secure/EmailOptOutSecure/ThankYouEmailSecure";
import { apiService } from "./domain/apiService";
import SignUpToken from "./components/containers/public/Login/SignUpToken/SignUpToken";
import SignUpQr from "./components/containers/public/Login/SignUpQr";
let ProbateCase;
if (process.env.FEATURE_PROBATE_CASE_SUBMISSION) {
    ProbateCase = require("./components/containers/secure/ProbateCase/ProbateCase").default;
}
let Disclaimers;
if (process.env.CLIENT === "CapOne") {
    Disclaimers = require("./components/containers/public/Disclaimers").default;
}
let EmailOptOutSecure;
EmailOptOutSecure = require("./components/containers/secure/EmailOptOutSecure/EmailOptOutSecure").default;
let EmailOptOutPublic;
EmailOptOutPublic = require("./components/containers/public/EmailOptOutPublic").default;
let ThankYouEmail;
ThankYouEmail = require("./components/containers/public/ThankYouEmail").default;
let Documents;
if (process.env.FEATURE_DOCUMENTS) {
    Documents = require("./components/containers/secure/Documents/Documents").default;
}
const Registration = process.env.FEATURE_MULTI_STEP_REGISTRATION === "true"
    ? require("./components/containers/public/Registration/Registration").default
    : require("./components/containers/public/AccountValidation/AccountValidation").default;
const requireProfile = (body) => (nextState, replace, callback) => {
    const { profile } = store.getState().profile;
    if (profile == null) {
        // needs a login to load the profile
        replace("/public");
        callback();
        return;
    }
    if (nextState.location.pathname.toLowerCase() !== "/secured/createprofile"
        && (!profile.isComplete
            || !Boolean(profile.city && profile.state && profile.zip))) {
        replace("/secured/createProfile");
        callback();
        return;
    }
    if (body) {
        body();
    }
    callback();
};
const authRedirect = (_nextState, replace, callback) => {
    const state = store.getState();
    const pathName = state.routing.locationBeforeTransitions.pathname.toLowerCase();
    if (pathName.includes("/disclaimers") || pathName.includes("/quickpay")) {
        callback();
        return;
    }
    const alreadyInSecured = pathName.includes("/secured");
    const alreadyInPublic = pathName.includes("/public");
    const loggedIn = state.auth.loggedIn && state.profile.profile != null;
    if (alreadyInSecured && !loggedIn) {
        replace("/public");
    }
    else if (!alreadyInSecured && loggedIn && pathName === "/public") {
        replace("/secured");
    }
    else if (!alreadyInSecured && !alreadyInPublic) {
        replace("/public");
    }
    callback();
};
const persist = persistStore(store, { blacklist: ["chat", "contact", "document", "routing", "payment"] }, async function callback() {
    const { i18n } = store.getState();
    let { locale } = i18n;
    if (i18n.useBrowserLocale) {
        const browserLocales = i18nUtils.getBrowserLocales();
        for (const browserLocale of browserLocales) {
            if (browserLocale in i18nUtils.VALID_LOCALES) {
                locale = browserLocale;
                break;
            }
        }
    }
    await store.dispatch(importLocale(locale || "en"));
    doRender();
});
const persistPurge = () => {
    persist.purge(Object.keys(store.getState()).filter(it => it !== "i18n"));
};
const history = syncHistoryWithStore(browserHistory, store);
history.listen(location => {
    ga("set", "page", location.pathname + location.search);
    ga("send", "pageview");
});
let didSendBeacon = false;
const beaconFunc = canSendBeacon() ? sendUnloadBeacon : sendUnloadXhr;
window.addEventListener("beforeunload", beaconFunc);
window.addEventListener("unload", beaconFunc);
function canSendBeacon() {
    if (!("navigator" in window)) {
        return false;
    }
    return typeof window.navigator.sendBeacon === "function";
}
function sendUnloadBeacon() {
    if (!didSendBeacon) {
        const xsrfToken = apiService.getXsrfToken();
        navigator.sendBeacon("/api/session/exitbeacon", xsrfToken);
        didSendBeacon = true;
    }
}
function sendUnloadXhr() {
    if (!didSendBeacon) {
        const xsrfToken = apiService.getXsrfToken();
        const xhr = new XMLHttpRequest();
        xhr.open("POST", "/api/session/exit", false);
        xhr.setRequestHeader("Accept", "*/*");
        xhr.setRequestHeader("X-XSRF-TOKEN", xsrfToken);
        xhr.send();
        didSendBeacon = true;
    }
}
function doRender() {
    /* mount and render the application */
    const rootElement = document.getElementById("js-root-container");
    /* Setup two routing trees(public and secured) with certain redirects for authentication purposes */
    render((<StoreProvider store={store}>
			<Router history={history}>
				<Route path="/" component={App} onEnter={authRedirect}>
					<Route path="/public" component={Public}>
						<IndexRoute component={Login}/>
						{process.env.CLIENT === "CapOne" && (<Route path="disclaimers" component={Disclaimers}/>)}
						<Route path="options" component={NewUserOptions}/>
						<Route path="unsubscribe" component={Unsubscribe}/>
						<Route path="quickpay/:token/:subpath" component={QuickPay}/>
						<Redirect from="quickpay/:token" to="quickpay/:token/pay"/>
						<Route path="validation" component={Registration} onEnter={() => store.dispatch(fetchQuestions())}/>
						<Route path="register/:token" component={SignUpToken}/>
						<Route path="registerqr/:referenceNumber/:pin" component={SignUpQr}/>
						{process.env.CLIENT !== "CapOne" && (<Route path="legal" component={LegalNotice}/>)}
						{process.env.CLIENT !== "CapOne" && (<Route path="accessibility" component={Accessibility}/>)}
						<Route path="refer" component={ReferAuthorizedUser}/>
						<Route path="forgotPassword" component={PasswordForgot} onEnter={persistPurge}/>
						{process.env.FEATURE_MULTI_STEP_REGISTRATION !== "true" && (<Route path="createPassword" component={PasswordCreate}/>)}
						<Route path="contact" component={ContactPublic}/>
						<Route path="emailOptOut" component={EmailOptOutPublic}/>
						<Route path="thankYou" component={ThankYou}/>
						<Route path="thankYouRefer" component={ThankYouRefer}/>
						<Route path="thankYouEmail" component={ThankYouEmail}/>
						<Redirect from="*" to="/public"/>
					</Route>
					<Route path="/secured" component={Secured} onEnter={() => {
        store.dispatch(checkLoggedInStatus());
    }}>
						<IndexRoute component={Dashboard} onEnter={requireProfile(() => {
        store.dispatch(loadAccts());
        store.dispatch(fetchQuestions());
    })}/>
						<Route path="changePassword" component={PasswordChange} onEnter={requireProfile()}/>
						<Route path="createProfile" component={ProfileCreate} onEnter={requireProfile()}/>
						<Route path="editProfile" component={ProfileEdit} onEnter={requireProfile()}/>
						<Route path="refer" component={ReferAuthorizedUser}/>
						<Route path="createPassword" component={PasswordCreate}/>
						<Route path="contact" component={ContactSecure}/>
						<Route path="emailOptOut" component={EmailOptOutSecure}/>
						<Route path="thankYouEmail" component={ThankYouEmailSecure}/>
						<Redirect from="payNow/:id" to="payNow/:id/payment"/>
						<Route path="payNow/:id/:step" component={PayNow}/>
						<Route path="receipt/:paymentId" component={Receipt}/>
						<Route path="account/:id" component={Account}/>
						<Route path="callback/:id" component={Callback}/>
						<Route path="settlement/:id" component={Settlement}/>
						{process.env.FEATURE_PROBATE_CASE_SUBMISSION && (<Route path="probateCase/:id" component={ProbateCase}/>)}
						{process.env.FEATURE_DOCUMENTS && (<Route path="documents" component={Documents}/>)}
						{process.env.CLIENT !== "CapOne" && (<Route path="disclosures" component={Disclosures}/>)}
						<Redirect from="*" to="/secured"/>
					</Route>
				</Route>
				<Redirect from="/q/:referenceNumber/:pin" to="/public/registerqr/:referenceNumber/:pin"/>
			</Router>
		</StoreProvider>), rootElement);
}
if (process.env.NODE_ENV !== "production") {
    const { selectLocale } = require("./domain/i18n/i18nActions");
    window.setLocale = (locale) => store.dispatch(selectLocale(locale));
}



// WEBPACK FOOTER //
// ./src/assets/scripts/main.tsx